/**
 * POSADA — who may comment on whose work, and what one member is allowed to know about another.
 *
 * Pure, like the rest of `core`: the client uses it to decide which buttons exist and the Worker
 * uses the same function to decide whether to write anything. One implementation, no drift.
 *
 * The mechanic itself lives almost entirely outside this app. The critique is written on Skool,
 * where the community already reads and moderates it; what crosses back is a claim that it
 * happened, aimed at one named person. So there is no text here, no moderation, and no score —
 * `rules.ts` still counts exactly two things, and nothing in this file adds a third.
 */

import type { ResolvedContent, ResolvedNode } from './content';
import { isNodeComplete, type PlayerState } from './rules';

/** How many peers are drawn around one marker before the rest become a "+N". */
export const MAX_PEERS_PER_MARKER = 4;

/** Peers stop being drawn once they have been away this long. The map shows who is around. */
export const PEER_ACTIVE_DAYS = 30;

/** A member as anyone other than themselves is allowed to see them. No email, ever. */
export interface Peer {
  id: string;
  /** Display name, or the part of the address before the @ when they have not set one. */
  name: string;
  look: number;
  souvenirs: string[];
  /** The node they are working on now — null when nothing on their map is open. */
  nodeId: string | null;
  islandId: string | null;
}

/** One posted work, as offered to somebody who might go and comment on it. */
export interface PeerWork {
  memberId: string;
  name: string;
  nodeId: string;
  url: string;
  postedAt: number;
  /** True once anyone has commented. The work is helped; it is no longer waiting. */
  answered: boolean;
  /** True when this viewer already claimed a comment on it. */
  claimed: boolean;
}

/** A comment somebody left on my work. The words are on Skool; this is the pointer to them. */
export interface InboxItem {
  id: string;
  nodeId: string;
  fromId: string;
  fromName: string;
  url: string;
  at: number;
  seenAt: number | null;
  thankedAt: number | null;
  missingAt: number | null;
}

/** A comment I left, coming back to me. `revisedAt` is the only reason this exists. */
export interface EchoItem {
  id: string;
  nodeId: string;
  toName: string;
  at: number;
  thankedAt: number | null;
  revisedAt: number | null;
}

export interface PosadaState {
  inbox: InboxItem[];
  echo: EchoItem[];
  /** False when this member has switched themselves out of the crew entirely. */
  visible: boolean;
}

export const emptyPosada = (): PosadaState => ({ inbox: [], echo: [], visible: true });

/**
 * Why this member cannot leave a comment on this work. Null means they can.
 *
 * Serbian kinds, like NodeStatus and BlockerKind — these are read out loud in code review by
 * people who speak the product's language, not the codebase's.
 */
export type CommentRefusal =
  | 'sam-sebi'
  | 'nisi-prosao'
  | 'vec-si'
  | 'popravljeno'
  | 'nije-okacen'
  | 'van-posade'
  | 'istekao';

export interface CommentCheck {
  /** The would-be commenter. */
  me: { id: string; readOnly: boolean; visible: boolean };
  /** Their progress, for the one rule that matters: have you been where they are. */
  state: PlayerState;
  target: { id: string; visible: boolean };
  work: { nodeId: string; postedAt: number | null; revisedAt: number | null; claimed: boolean };
}

/**
 * The one rule: **you may comment on a work if you have finished that node yourself.**
 *
 * Not "if you are two islands ahead", not "if you have posted N works" — those would be a rank in
 * disguise. Having done the thing is the whole qualification, and it is the same qualification a
 * person applies when they decide whether they have anything useful to say.
 */
export function commentRefusal(
  content: ResolvedContent,
  check: CommentCheck,
): CommentRefusal | null {
  const { me, state, target, work } = check;
  if (me.readOnly) return 'istekao';
  if (me.id === target.id) return 'sam-sebi';
  if (!me.visible || !target.visible) return 'van-posade';
  if (!work.postedAt) return 'nije-okacen';
  // Nothing left to unblock: they already did the step this comment exists to make possible.
  if (work.revisedAt) return 'popravljeno';
  if (work.claimed) return 'vec-si';

  const node = findNode(content, work.nodeId);
  // A node this viewer's path does not contain is not something they can have an opinion on.
  if (!node) return 'nisi-prosao';
  if (!isNodeComplete(node, state.progress[node.id])) return 'nisi-prosao';
  return null;
}

export function findNode(content: ResolvedContent, nodeId: string): ResolvedNode | null {
  for (const island of content.islands) {
    const node = island.nodes.find((n) => n.id === nodeId);
    if (node) return node;
  }
  return null;
}

/**
 * The peers this viewer is allowed to see, which is the same rule the map already obeys: an island
 * they have not reached is open sea, and nothing — not a grey shape, not a stranger's hat — is
 * drawn out there (GDD §3). Progressive disclosure survives the crew for free, and nobody learns
 * that island five exists by meeting somebody who is standing on it.
 *
 * It also sorts the paths out without a special case. ADOBE and DAVINCI share node ids, so a
 * DaVinci editor shows up on an Adobe map standing at the same task in the other program, which is
 * exactly right. KREATOR node ids do not exist in either, so those peers simply fall out.
 */
export function visibleIslandPeers(
  content: ResolvedContent,
  peers: Peer[],
  visibleIslandIds: ReadonlySet<string>,
  excludeId: string,
): Peer[] {
  const out: Peer[] = [];
  for (const peer of peers) {
    if (peer.id === excludeId) continue;
    if (!peer.islandId || !visibleIslandIds.has(peer.islandId)) continue;
    const island = content.islands.find((i) => i.id === peer.islandId);
    if (!island) continue;
    // A node that this path does not contain would have nowhere to stand on this map. Drop the
    // node, keep the person: they are still on that island, and the dock is a true place.
    const nodeId = peer.nodeId && island.nodes.some((n) => n.id === peer.nodeId) ? peer.nodeId : null;
    out.push({ ...peer, nodeId });
  }
  return sortPeers(out);
}

/**
 * The works this member could go and help with, best first.
 *
 * Three keys, in this order, and the order is the argument:
 *
 *   1. **nobody has commented yet.** The person who posted five days ago and heard nothing is the
 *      one about to give up. Works that already have a comment sink, but never disappear — a
 *      second opinion is worth having.
 *   2. **a comment there unblocks a revision.** On a node whose fourth step is the revision, a
 *      comment does mechanical work: it opens the door the whole product is built around. On
 *      „Objavi poruku dobrodošlice" it is a friendly reply and nothing more. Both are worth
 *      writing; only one of them is worth writing *first*.
 *   3. **the longest wait.** Same work order the mentor queue uses.
 *
 * Without key 2 this list fills with introductions, because everybody posts one and everybody is
 * qualified to answer it — the least useful comment in the curriculum, crowding out the most.
 */
export function commentable(
  content: ResolvedContent,
  check: Omit<CommentCheck, 'work' | 'target'>,
  works: PeerWork[],
): PeerWork[] {
  const open = works.filter(
    (work) =>
      commentRefusal(content, {
        ...check,
        target: { id: work.memberId, visible: true },
        work: { nodeId: work.nodeId, postedAt: work.postedAt, revisedAt: null, claimed: work.claimed },
      }) === null,
  );
  const unblocks = (work: PeerWork): boolean => findNode(content, work.nodeId)?.needsRevision === true;
  return open.sort((a, b) => {
    if (a.answered !== b.answered) return a.answered ? 1 : -1;
    if (unblocks(a) !== unblocks(b)) return unblocks(a) ? -1 : 1;
    return a.postedAt - b.postedAt;
  });
}

/** Comments that arrived and have not been shown yet — what the card announces on the way in. */
export function unseenInbox(posada: PosadaState): InboxItem[] {
  return posada.inbox.filter((item) => !item.seenAt && !item.missingAt);
}

/** My comments that produced something. The payoff, and the only reward in the feature. */
export function unseenEcho(posada: PosadaState): EchoItem[] {
  return posada.echo.filter((item) => item.revisedAt || item.thankedAt);
}

/** Every comment left on one node of mine, newest first. */
export function inboxFor(posada: PosadaState, nodeId: string): InboxItem[] {
  return posada.inbox.filter((item) => item.nodeId === nodeId).sort((a, b) => b.at - a.at);
}

/**
 * A stable pseudo-random ordinal for a member.
 *
 * Used to pick which patch of grass a peer stands on beside a marker. It has to be stable — a
 * person who jumps to the other side of a signpost every time the map redraws reads as a glitch,
 * not as a person — and it must not be their position in any list, or the order would leak
 * something (who joined first, who is furthest) that this feature deliberately does not rank.
 *
 * FNV-1a, 32-bit. Nothing here is a secret; it only needs to be well spread and identical on both
 * halves of the app.
 */
export function peerOrdinal(memberId: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < memberId.length; i++) {
    hash ^= memberId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Deterministic order for peers standing at the same marker, so the crowd never reshuffles. */
export function sortPeers(peers: Peer[]): Peer[] {
  return [...peers].sort((a, b) => peerOrdinal(a.id) - peerOrdinal(b.id) || (a.id < b.id ? -1 : 1));
}
